'use client'

import { useEffect, useState, type RefObject } from 'react'
import type { Rect } from './boardGeometry'
import { houseFrame } from './stageFrame'

const EMPTY: Rect = { x: 0, y: 0, width: 0, height: 0 }

const sameRect = (a: Rect, b: Rect): boolean =>
  a.x === b.x && a.y === b.y && a.width === b.width && a.height === b.height

/**
 * The picture box, in coordinates RELATIVE TO `containerRef` (which the board
 * mounts as `position: absolute; inset: 0` inside `.dolby-feed`, so the
 * container rect is the feed rect and the returned x/y are the letterbox bars).
 *
 * ONE input, ONE observer (B30). The stage is measured; the clip is not. Before
 * B30 this hook hunted the `<video>` element on a 500 ms interval and read its
 * natural size off `loadedmetadata` / `resize` / `emptied`, which made the
 * board's origin and its scale a function of whatever the stream happened to
 * report — a late report moved the board once, an error-and-remount moved it
 * again, and a non-16:9 encode moved it for good. No other surface in the widget
 * works that way. `houseFrame` derives the same rect from the container alone.
 *
 * The `ResizeObserver` covers window resizes, the mobile `--bottom-panel-height`
 * shuffle and the social-focus corner mode (`.dolby-feed--stream-cornered`),
 * none of which a window resize event reports; the `window` listener is only the
 * fallback for an environment without the observer.
 *
 * Every DOM global is guarded: the board's host is a client component but the
 * module is imported into a server-rendered tree.
 */
export function useStageFrame(containerRef: RefObject<HTMLElement | null>): Rect {
  const [frame, setFrame] = useState<Rect>(EMPTY)

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    const measure = (): void => {
      const box = container.getBoundingClientRect()
      const next = houseFrame({ width: box.width, height: box.height })
      setFrame((prev) => (sameRect(prev, next) ? prev : next))
    }

    measure()

    let observer: ResizeObserver | null = null
    let windowListener = false
    if (typeof ResizeObserver !== 'undefined') {
      observer = new ResizeObserver(measure)
      observer.observe(container)
    } else if (typeof window !== 'undefined') {
      window.addEventListener('resize', measure)
      windowListener = true
    }

    return () => {
      if (observer) observer.disconnect()
      if (windowListener) window.removeEventListener('resize', measure)
    }
  }, [containerRef])

  return frame
}
